import React from 'react';
import { motion } from 'framer-motion';
import { Target, Clock, CheckCircle2, Lock, Play, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const tests = [
  { id: 1, title: 'Module 1-2 Comprehensive Test', questions: 30, duration: '45 min', status: 'completed', score: 85, passMark: 70, date: '2026-04-20' },
  { id: 2, title: 'Mid-Course Assessment', questions: 50, duration: '60 min', status: 'completed', score: 78, passMark: 70, date: '2026-04-28' },
  { id: 3, title: 'Module 3-4 Evaluation', questions: 40, duration: '50 min', status: 'available', score: null, passMark: 70, date: null },
  { id: 4, title: 'Final Mock Examination', questions: 80, duration: '120 min', status: 'locked', score: null, passMark: 75, date: null },
];

export default function MockTests() {
  const passed = tests.filter(t => t.status === 'completed' && t.score >= t.passMark).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Mock Tests</h2>
          <p className="text-muted-foreground text-sm mt-1">{passed} passed · {tests.filter(t => t.status === 'completed').length} attempted</p>
        </div>
      </div>

      <div className="grid gap-4">
        {tests.map((test, i) => {
          const isPassed = test.score !== null && test.score >= test.passMark;
          const isFailed = test.score !== null && test.score < test.passMark;
          return (
            <motion.div key={test.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
              whileHover={test.status !== 'locked' ? { y: -2, transition: { duration: 0.2 } } : {}}
              className={`glass rounded-2xl border p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center ${test.status === 'locked' ? 'opacity-50 border-white/80' : 'border-white/80 hover:border-primary/20'} transition-colors`}>
              {/* Icon */}
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                isPassed ? 'bg-green-100' : isFailed ? 'bg-red-100' : test.status === 'available' ? 'bg-primary/15' : 'bg-muted'
              }`}>
                {test.status === 'locked' ? <Lock className="w-5 h-5 text-muted-foreground" /> :
                 isPassed ? <CheckCircle2 className="w-6 h-6 text-green-500" /> :
                 isFailed ? <Target className="w-6 h-6 text-red-500" /> :
                 <Target className="w-6 h-6 text-primary" />}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-bold text-base">{test.title}</h3>
                <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3 text-primary" />{test.questions} questions</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-primary" />{test.duration}</span>
                  <span>Pass mark: {test.passMark}%</span>
                  {test.date && <span>{new Date(test.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                </div>
                {test.score !== null && (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden max-w-[180px]">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${test.score}%` }} transition={{ duration: 0.8, delay: i * 0.1 + 0.4 }}
                        className={`h-full rounded-full ${isPassed ? 'bg-green-500' : 'bg-red-500'}`} />
                    </div>
                    <span className={`text-sm font-bold ${isPassed ? 'text-green-600' : 'text-red-500'}`}>{test.score}%</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isPassed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {isPassed ? 'PASSED' : 'FAILED'}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex-shrink-0">
                {test.status === 'completed' ? (
                  <Button size="sm" variant="outline" className="rounded-xl text-xs font-semibold border-black/15 hover:bg-primary/10 gap-1.5">
                    <BarChart3 className="w-3.5 h-3.5" /> Review
                  </Button>
                ) : test.status === 'available' ? (
                  <Button size="sm" className="rounded-xl text-xs font-semibold bg-primary text-primary-foreground shadow-md shadow-primary/25 hover:bg-primary/90 gap-1.5">
                    <Play className="w-3.5 h-3.5" /> Start Test
                  </Button>
                ) : (
                  <Button size="sm" disabled variant="ghost" className="rounded-xl text-xs gap-1.5">
                    <Lock className="w-3.5 h-3.5" /> Locked
                  </Button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}